/**
 * FINANCING MARKETPLACE (UF-06). Browse loan products, not banks.
 *
 * The other side of the two-sided credit marketplace: instead of searching institutions, the sponsor
 * browses the products DCPs publish, filters by category, and starts a request from any of them. Each
 * product carries what a borrower actually compares on: rate, max LTV, tenure, amount band, repayment,
 * supported assets, provider rating, approval time, and the documents required.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type FinanceProduct } from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';

export default function Marketplace() {
  const [products, setProducts] = useState<FinanceProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cat, setCat] = useState('All');

  const load = useCallback(async () => {
    setError(null);
    try {
      setProducts((await api.finance.products()).products);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load the marketplace.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(
    () => (products ? ['All', ...Array.from(new Set(products.map((p) => p.category)))] : ['All']),
    [products],
  );
  const shown = useMemo(
    () => (products ? products.filter((p) => cat === 'All' || p.category === cat) : []),
    [products, cat],
  );

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!products) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/credit"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Credit
      </Link>

      <PageHeader
        title="Financing marketplace"
        subtitle="Compare loan products from accredited credit providers."
      />

      {/* CATEGORY FILTER. */}
      <div
        className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
        role="tablist"
        aria-label="Product categories"
      >
        {categories.map((c) => (
          <button
            key={c}
            role="tab"
            aria-selected={cat === c}
            onClick={() => setCat(c)}
            className={`shrink-0 rounded-pill px-3 py-1.5 text-micro font-medium transition ${
              cat === c
                ? 'bg-primary text-white'
                : 'bg-surface-sunken text-ink-muted hover:text-ink'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <ul className="space-y-3">
        {shown.map((p) => (
          <li key={p.id}>
            <Reveal>
              <div className="rounded-card border border-border bg-surface p-4 shadow-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                    <p className="text-micro text-ink-subtle">
                      {p.provider} · <RatingStars rating={p.providerRating} />{' '}
                      {p.providerRating.toFixed(1)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-muted">
                    {p.category}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3 border-t border-border pt-3 sm:grid-cols-4">
                  <Spec label="Interest" value={`${p.interestPct}%`} accent />
                  <Spec label="Max LTV" value={`${p.maxLtvPct}%`} />
                  <Spec label="Tenure" value={`${Math.round(p.tenureMonths / 12)} yr`} />
                  <Spec label="Repayment" value={p.repayment} />
                  <Spec label="Min value" value={`KES ${p.minValue}`} />
                  <Spec label="Max amount" value={`KES ${p.maxAmount}`} />
                  <Spec label="Approval" value={`${p.approvalDays} days`} />
                  <Spec label="Assets" value={p.supportedAssets.join(', ')} />
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.docs.map((d) => (
                    <span
                      key={d}
                      className="rounded-pill bg-surface-sunken px-2 py-0.5 text-nano font-medium text-ink-subtle"
                    >
                      {d}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/credit/apply?product=${p.id}`}
                  className="mt-3 block w-full rounded-field bg-primary py-2.5 text-center text-sm font-medium text-white transition hover:bg-primary-light"
                >
                  Request this financing
                </Link>
              </div>
            </Reveal>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Spec({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className={`truncate text-sm font-medium ${accent ? 'text-brand' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <span
      className="inline-flex align-middle text-warning"
      aria-label={`Rated ${rating.toFixed(1)} out of 5`}
    >
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
      </svg>
    </span>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
